import { Injectable, Injector } from "@angular/core";
import {
  DocumentTemplate,
  NewDocumentTemplate,
} from "../../models/document-template.model";
import {
  DocumentTemplateEndorsement,
  NewDocumentTemplateEndorsement,
} from "../../models/document-template-endorsement.model";
import { Observable, of, throwError } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { BaseService } from "../../../shared/services/base.service";

@Injectable({
  providedIn: "root",
})
export class DocumentTemplateService extends BaseService {
  className = "DocumentTemplateService";

  constructor(injector: Injector) {
    super(injector);
  }

  getTemplates(): Observable<DocumentTemplate[]> {
    return this.getWithoutErrorHandling<DocumentTemplate[]>(`/v1/templates`);
  }

  getTemplateEndorsements(
    templateId: number
  ): Observable<DocumentTemplateEndorsement[]> {
    return this.get<DocumentTemplateEndorsement[]>(
      `/v1/templates/${templateId}/endorsements`
    );
  }

  saveDocumentTemplate(
    model: NewDocumentTemplate
  ): Observable<DocumentTemplate> {
    return this.post<NewDocumentTemplate>(`/v1/templates`, model);
  }

  saveDocumentTemplateEndorsements(
    templateId: number,
    model: Array<NewDocumentTemplateEndorsement>
  ): Observable<Array<DocumentTemplateEndorsement>> {
    return this.put<Array<NewDocumentTemplateEndorsement>>(
      `/v1/templates/${templateId}/endorsements`,
      model
    );
  }

  deleteTemplate(templateId: number): Observable<object> {
    return this.delete(`/v1/templates/${templateId}`);
  }

  exportTemplateAsJson(templateId: number): Observable<string> {
    return this.getTemplateEndorsements(templateId).pipe(
      map(endorsements => {
        const exportData = {
          templateId,
          exportDate: new Date().toISOString(),
          endorsements: endorsements.map(e => ({
            participantRole: e.participantRole,
            roleSequence: e.roleSequence,
            endorsementType: e.endorsementType,
            xPosition: e.xPosition,
            yPosition: e.yPosition,
            height: e.height,
            width: e.width,
            pageNumber: e.pageNumber,
            signatureReference: e.signatureReference,
            isRequired: e.isRequired,
            format: e.format
          }))
        };
        return JSON.stringify(exportData, null, 2);
      })
    );
  }

  importTemplateFromJson(templateName: string, jsonData: string): Observable<DocumentTemplate> {
    try {
      const importData = JSON.parse(jsonData);
      
      if (!importData || typeof importData !== 'object') {
        return throwError(() => new Error('Invalid template data format'));
      }

      const newTemplate = new NewDocumentTemplate();
      newTemplate.name = templateName;

      return this.saveDocumentTemplate(newTemplate).pipe(
        switchMap(savedTemplate => {
          if (importData.endorsements && Array.isArray(importData.endorsements) && importData.endorsements.length > 0) {
            const endorsements = importData.endorsements.map((e: any) => {
              const endorsement = new NewDocumentTemplateEndorsement();
              endorsement.participantRole = e.participantRole;
              endorsement.roleSequence = e.roleSequence;
              endorsement.endorsementType = e.endorsementType;
              endorsement.xPosition = e.xPosition;
              endorsement.yPosition = e.yPosition;
              endorsement.height = e.height;
              endorsement.width = e.width;
              endorsement.pageNumber = e.pageNumber;
              endorsement.signatureReference = e.signatureReference || '';
              endorsement.status = "SIGNABLE";
              endorsement.isRequired = e.isRequired;
              endorsement.format = e.format;
              endorsement.templateId = savedTemplate.id!;
              return endorsement;
            });

            return this.saveDocumentTemplateEndorsements(savedTemplate.id!, endorsements).pipe(
              map(() => savedTemplate)
            );
          }
          return of(savedTemplate);
        })
      );
    } catch (error) {
      return throwError(() => new Error('Invalid template data format'));
    }
  }
}
